import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Users, CreditCard, Star, Trash2, MessageCircle, CheckCircle, Mail, Layers, Briefcase, Flag } from 'lucide-react';
import axios from 'axios';

const resolveName = (nameObj, lang) =>
  nameObj?.[lang] ?? nameObj?.['es'] ?? nameObj?.['en'] ?? '';

const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-800',
  negotiating: 'bg-amber-100 text-amber-800',
  provider_accepted: 'bg-blue-100 text-blue-800',
  payment_pending: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-green-100 text-green-800',
  awaiting_confirmation: 'bg-orange-100 text-orange-800',
  completed: 'bg-gray-100 text-gray-700',
  cancelled: 'bg-gray-100 text-gray-500',
  disputed: 'bg-red-100 text-red-700',
};

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const AdminManagementPage = () => {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [supportMessages, setSupportMessages] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Jobs tab state
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [jobsTotal, setJobsTotal] = useState(0);
  const [jobStatusFilter, setJobStatusFilter] = useState('');
  const [jobPage, setJobPage] = useState(1);
  const [chatJob, setChatJob] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);

  // Verifications tab state
  const [verifQueue, setVerifQueue] = useState([]);
  const [verifLoading, setVerifLoading] = useState(false);
  const [verifAction, setVerifAction] = useState(null); // { userId, name, action: 'approve'|'reject' }
  const [verifGrantLicense, setVerifGrantLicense] = useState(false);
  const [verifGrantBusiness, setVerifGrantBusiness] = useState(false);
  const [verifRejectReason, setVerifRejectReason] = useState('');
  const [verifSubmitting, setVerifSubmitting] = useState(false);

  // Client verifications tab state
  const [clientVerifQueue, setClientVerifQueue] = useState([]);
  const [clientVerifLoading, setClientVerifLoading] = useState(false);
  const [clientVerifAction, setClientVerifAction] = useState(null); // { userId, name } when rejecting
  const [clientRejectReason, setClientRejectReason] = useState('');

  // Dispute resolution state
  const [disputeJob, setDisputeJob] = useState(null);
  const [disputeOutcome, setDisputeOutcome] = useState('favour_client');
  const [disputeNote, setDisputeNote] = useState('');
  const [disputeSubmitting, setDisputeSubmitting] = useState(false);

  // Flags tab state
  const [flags, setFlags] = useState([]);
  const [flagsLoading, setFlagsLoading] = useState(false);
  const [suspendConfirm, setSuspendConfirm] = useState(null); // flag object to suspend

  const fetchFlags = useCallback(async () => {
    setFlagsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/flags`);
      setFlags(res.data);
    } catch {
      toast.error('Failed to load flags');
    } finally {
      setFlagsLoading(false);
    }
  }, []);

  const handleFlagAction = async (flagId, action) => {
    try {
      await axios.post(`${API}/admin/flags/${flagId}/review`, { action });
      toast.success(action === 'dismiss' ? t('admin.dismiss') : t('admin.suspend_user'));
      setSuspendConfirm(null);
      fetchFlags();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Action failed');
    }
  };

  const fetchVerifQueue = useCallback(async () => {
    setVerifLoading(true);
    try {
      const res = await axios.get(`${API}/admin/providers/verification-queue`);
      setVerifQueue(res.data);
    } catch {
      toast.error('Failed to load verification queue');
    } finally {
      setVerifLoading(false);
    }
  }, []);

  const fetchClientVerifQueue = useCallback(async () => {
    setClientVerifLoading(true);
    try {
      const res = await axios.get(`${API}/admin/clients/verification-queue`);
      setClientVerifQueue(res.data);
    } catch {
      toast.error('Failed to load client verification queue');
    } finally {
      setClientVerifLoading(false);
    }
  }, []);

  const handleClientVerifAction = async (userId, action, rejectionReason) => {
    try {
      const body = action === 'approve'
        ? { action: 'approve' }
        : { action: 'reject', rejection_reason: rejectionReason };
      await axios.post(`${API}/admin/clients/${userId}/verify`, body);
      toast.success(`Client ${action}d`);
      setClientVerifAction(null);
      setClientRejectReason('');
      fetchClientVerifQueue();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Action failed');
    }
  };

  const handleVerifAction = async () => {
    if (!verifAction) return;
    setVerifSubmitting(true);
    try {
      const body = verifAction.action === 'approve'
        ? { action: 'approve', tier_granted: 2, grant_license: verifGrantLicense, grant_business: verifGrantBusiness }
        : { action: 'reject', rejection_reason: verifRejectReason };
      await axios.post(`${API}/admin/providers/${verifAction.userId}/verify`, body);
      toast.success(`Provider ${verifAction.action}d`);
      setVerifAction(null);
      setVerifRejectReason('');
      setVerifGrantLicense(false);
      setVerifGrantBusiness(false);
      fetchVerifQueue();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Action failed');
    } finally {
      setVerifSubmitting(false);
    }
  };

  const handleViewDocuments = async (providerId) => {
    try {
      const res = await axios.get(`${API}/admin/providers/${providerId}/documents`);
      const docs = res.data;
      if (!docs || docs.length === 0) {
        toast.info('No documents uploaded yet');
        return;
      }
      docs.forEach(doc => {
        if (doc.signed_url) {
          window.open(doc.signed_url, '_blank', 'noopener,noreferrer');
        }
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load documents');
    }
  };

  const handleResolveDispute = async () => {
    if (!disputeJob) return;
    setDisputeSubmitting(true);
    try {
      await axios.post(`${API}/admin/jobs/${disputeJob.id}/resolve-dispute`, {
        outcome: disputeOutcome,
        resolution_note: disputeNote,
      });
      toast.success('Dispute resolved');
      setDisputeJob(null);
      setDisputeNote('');
      setDisputeOutcome('favour_client');
      fetchJobs(jobStatusFilter, jobPage);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to resolve dispute');
    } finally {
      setDisputeSubmitting(false);
    }
  };

  const fetchJobs = useCallback(async (status = '', page = 1) => {
    setJobsLoading(true);
    try {
      const params = { page, limit: 20 };
      if (status) params.status_filter = status;
      const res = await axios.get(`${API}/admin/jobs`, { params });
      setJobs(res.data.jobs);
      setJobsTotal(res.data.total);
    } catch {
      toast.error('Failed to load jobs');
    } finally {
      setJobsLoading(false);
    }
  }, []);

  const openChat = async (job) => {
    setChatJob(job);
    setChatLoading(true);
    try {
      const res = await axios.get(`${API}/admin/jobs/${job.id}/messages`);
      setChatMessages(res.data);
    } catch {
      toast.error('Failed to load messages');
    } finally {
      setChatLoading(false);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const [usersRes, transactionsRes, reviewsRes, statsRes, supportRes] = await Promise.all([
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/transactions`),
        axios.get(`${API}/admin/reviews`),
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/support-messages`)
      ]);

      setUsers(usersRes.data);
      setTransactions(transactionsRes.data);
      setReviews(reviewsRes.data);
      setStats(statsRes.data);
      setSupportMessages(supportRes.data);
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [user, navigate, fetchData]);

  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/status?active=${!currentStatus}`);
      toast.success('User status updated');
      await fetchData();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const deleteReview = async (reviewId) => {
    if (!window.confirm('Delete this review?')) return;
    
    try {
      await axios.delete(`${API}/admin/reviews/${reviewId}`);
      toast.success('Review deleted');
      await fetchData();
    } catch (error) {
      toast.error('Failed to delete review');
    }
  };

  const resolveMessage = async (messageId) => {
    try {
      await axios.put(`${API}/admin/support-messages/${messageId}/resolve`);
      toast.success('Message marked as resolved');
      await fetchData();
    } catch (error) {
      toast.error('Failed to resolve message');
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <>
    <div className="min-h-screen bg-background">
      <nav className="bg-white border-b">
        <div className="container mx-auto px-4 py-4">
          <Button variant="ghost" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Dashboard
          </Button>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <h1 className="text-4xl font-bold mb-8" style={{ fontFamily: 'Outfit' }}>
          Admin Management
        </h1>

        {stats && (
          <div className="grid md:grid-cols-4 gap-6 mb-8">
            <Card>
              <CardContent className="pt-6">
                <div className="text-3xl font-bold mb-2">{stats.total_users}</div>
                <div className="text-sm text-muted-foreground">Total Users</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-3xl font-bold mb-2">{stats.total_providers}</div>
                <div className="text-sm text-muted-foreground">Providers</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-3xl font-bold mb-2">{stats.total_transactions}</div>
                <div className="text-sm text-muted-foreground">Transactions</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-3xl font-bold mb-2" style={{ color: 'hsl(120 100% 30%)' }}>
                  ${stats.total_revenue.toFixed(2)}
                </div>
                <div className="text-sm text-muted-foreground">Platform Revenue</div>
              </CardContent>
            </Card>
          </div>
        )}

        <Tabs defaultValue="users" className="w-full">
          <TabsList>
            <TabsTrigger value="users">
              <Users className="w-4 h-4 mr-2" />
              Users
            </TabsTrigger>
            <TabsTrigger value="transactions">
              <CreditCard className="w-4 h-4 mr-2" />
              Transactions
            </TabsTrigger>
            <TabsTrigger value="reviews">
              <Star className="w-4 h-4 mr-2" />
              Reviews
            </TabsTrigger>
            <TabsTrigger value="support">
              <MessageCircle className="w-4 h-4 mr-2" />
              Support {supportMessages.filter(m => !m.resolved).length > 0 && `(${supportMessages.filter(m => !m.resolved).length})`}
            </TabsTrigger>
            <TabsTrigger value="categories" onClick={() => navigate('/admin/categories')}>
              <Layers className="w-4 h-4 mr-2" />
              Categories
            </TabsTrigger>
            <TabsTrigger value="jobs" onClick={() => { if (jobs.length === 0) fetchJobs(jobStatusFilter, jobPage); }}>
              <Briefcase className="w-4 h-4 mr-2" />
              {t('admin.jobs')}
            </TabsTrigger>
            <TabsTrigger value="verifications" onClick={() => { if (verifQueue.length === 0) fetchVerifQueue(); }}>
              <CheckCircle className="w-4 h-4 mr-2" />
              {t('admin.verifications')}
            </TabsTrigger>
            <TabsTrigger value="client-verifications" onClick={() => { if (clientVerifQueue.length === 0) fetchClientVerifQueue(); }}>
              <CheckCircle className="w-4 h-4 mr-2" />
              {t('admin.client_verifications')}
            </TabsTrigger>
            <TabsTrigger value="flags" onClick={() => { if (flags.length === 0) fetchFlags(); }}>
              <Flag className="w-4 h-4 mr-2" />
              {t('admin.flags')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="users" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>User Management</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {users.map((u) => (
                    <div key={u.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <div className="font-semibold">{u.name}</div>
                        <div className="text-sm text-muted-foreground">{u.email}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge variant={u.role === 'admin' ? 'default' : 'outline'}>
                          {u.role}
                        </Badge>
                        <Badge variant={u.active === false ? 'destructive' : 'default'}>
                          {u.active === false ? 'Inactive' : 'Active'}
                        </Badge>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => toggleUserStatus(u.id, u.active !== false)}
                        >
                          {u.active === false ? 'Activate' : 'Deactivate'}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="transactions" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Transaction History</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {transactions.map((tx) => (
                    <div key={tx.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <div className="font-semibold">{tx.client_name} → {tx.provider_name}</div>
                        <div className="text-sm text-muted-foreground">
                          {new Date(tx.created_at).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold">${tx.amount}</div>
                        <div className="text-sm text-muted-foreground">
                          Fee: ${tx.commission_amount.toFixed(2)}
                        </div>
                        <Badge variant={tx.status === 'captured' ? 'default' : 'outline'}>
                          {tx.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                  {transactions.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">No transactions yet</div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="reviews" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Reviews & Ratings Management</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {reviews.map((review) => (
                    <div key={review.id} className="flex items-start justify-between p-4 border rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="flex">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star
                                key={star}
                                className="w-4 h-4"
                                fill={star <= review.rating ? '#FFCC00' : 'none'}
                                stroke={star <= review.rating ? '#FFCC00' : '#ccc'}
                              />
                            ))}
                          </div>
                          <span className="text-sm text-muted-foreground">
                            {new Date(review.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="font-semibold text-sm mb-1">
                          {review.client_name} → {review.provider_name}
                        </div>
                        <div className="text-sm">{review.comment}</div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => deleteReview(review.id)}
                        className="text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  {reviews.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">No reviews yet</div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="support" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Support Messages</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {supportMessages.map((msg) => (
                    <div key={msg.id} className="p-4 border rounded-lg" data-testid={`support-msg-${msg.id}`}>
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-muted-foreground" />
                          <div>
                            <div className="font-semibold">{msg.name}</div>
                            <div className="text-sm text-muted-foreground">{msg.email}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {msg.resolved ? (
                            <Badge variant="outline" className="bg-green-50">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Resolved
                            </Badge>
                          ) : (
                            <Badge variant="destructive">Pending</Badge>
                          )}
                          <span className="text-xs text-muted-foreground">
                            {new Date(msg.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      <div className="mb-2">
                        <div className="font-medium text-sm mb-1">Subject: {msg.subject}</div>
                        <div className="text-sm text-muted-foreground">{msg.message}</div>
                      </div>
                      {!msg.resolved && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => resolveMessage(msg.id)}
                          className="mt-2"
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Mark as Resolved
                        </Button>
                      )}
                    </div>
                  ))}
                  {supportMessages.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">No support messages yet</div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="jobs" className="mt-6">
            {/* Filter bar */}
            <div className="flex gap-3 mb-4">
              <Select value={jobStatusFilter} onValueChange={(v) => {
                const val = v === 'all' ? '' : v;
                setJobStatusFilter(val);
                setJobPage(1);
                fetchJobs(val, 1);
              }}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {['pending','negotiating','provider_accepted','payment_pending','in_progress','awaiting_confirmation','completed','cancelled','disputed'].map(s => (
                    <SelectItem key={s} value={s}>{s.replace(/_/g, ' ')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={() => fetchJobs(jobStatusFilter, jobPage)}>Refresh</Button>
            </div>

            <Card>
              <CardContent className="p-0">
                {jobsLoading ? (
                  <div className="py-12 text-center text-muted-foreground">Loading jobs…</div>
                ) : jobs.length === 0 ? (
                  <div className="py-12 text-center text-muted-foreground">No jobs found</div>
                ) : (
                  <div className="divide-y">
                    {jobs.map(job => {
                      const isDisputed = job.status === 'disputed';
                      const catLabel = [
                        resolveName(job.category_name, i18n.language),
                        resolveName(job.subcategory_name, i18n.language),
                      ].filter(Boolean).join(' › ');
                      return (
                        <div key={job.id} className={`flex items-center justify-between px-4 py-3 gap-4 ${isDisputed ? 'bg-red-50' : ''}`}>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[job.status] || 'bg-gray-100 text-gray-700'}`}>
                                {job.status}
                              </span>
                              {isDisputed && (
                                <span className="text-xs font-semibold text-red-700">{t('admin.disputed_jobs')}</span>
                              )}
                            </div>
                            <p className="text-sm font-medium truncate">{catLabel || '—'}</p>
                            <p className="text-xs text-muted-foreground">
                              {job.client_name} → {job.provider_name || t('admin.unassigned')}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {job.created_at ? new Date(job.created_at).toLocaleDateString(i18n.language) : ''}
                              {job.proposal_count > 0 && ` · ${job.proposal_count} proposals`}
                            </p>
                          </div>
                          <div className="flex gap-2 flex-shrink-0">
                            {job.status === 'disputed' && (
                              <Button size="sm" variant="destructive"
                                onClick={() => { setDisputeJob(job); setDisputeOutcome('favour_client'); setDisputeNote(''); }}>
                                {t('admin.resolve_dispute')}
                              </Button>
                            )}
                            <Button variant="outline" size="sm" onClick={() => openChat(job)}>
                              {t('admin.view_chat')}
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Pagination */}
            {jobsTotal > 20 && (
              <div className="flex justify-center gap-2 mt-4">
                <Button variant="outline" size="sm" disabled={jobPage <= 1}
                  onClick={() => { const p = jobPage - 1; setJobPage(p); fetchJobs(jobStatusFilter, p); }}>
                  Previous
                </Button>
                <span className="text-sm self-center">Page {jobPage} of {Math.ceil(jobsTotal / 20)}</span>
                <Button variant="outline" size="sm" disabled={jobPage >= Math.ceil(jobsTotal / 20)}
                  onClick={() => { const p = jobPage + 1; setJobPage(p); fetchJobs(jobStatusFilter, p); }}>
                  Next
                </Button>
              </div>
            )}
          </TabsContent>

          {/* ── Verifications tab ── */}
          <TabsContent value="verifications" className="mt-6">
            {verifLoading ? (
              <p className="text-center text-muted-foreground py-8">Loading…</p>
            ) : verifQueue.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">{t('verification.queue_empty')}</p>
            ) : (
              <Card>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-gray-50 text-xs text-muted-foreground">
                          <th className="text-left px-4 py-3">Provider</th>
                          <th className="text-left px-4 py-3">Current tier</th>
                          <th className="text-left px-4 py-3">Requested</th>
                          <th className="text-left px-4 py-3">Submitted</th>
                          <th className="text-left px-4 py-3">ID type</th>
                          <th className="text-left px-4 py-3">ID number</th>
                          <th className="text-left px-4 py-3">License</th>
                          <th className="text-left px-4 py-3">Business reg.</th>
                          <th className="text-left px-4 py-3">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {verifQueue.map(item => (
                          <tr key={item.user_id} className="border-b last:border-0 hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <div className="font-medium">{item.name}</div>
                              <div className="text-xs text-muted-foreground">{item.email}</div>
                            </td>
                            <td className="px-4 py-3">{item.current_tier}</td>
                            <td className="px-4 py-3 font-semibold">{item.tier_requested}</td>
                            <td className="px-4 py-3 text-xs">{item.submitted_at ? new Date(item.submitted_at).toLocaleDateString(i18n.language) : '—'}</td>
                            <td className="px-4 py-3">{item.submission?.id_document_type || '—'}</td>
                            <td className="px-4 py-3 font-mono text-xs">{item.submission?.id_document_number || '—'}</td>
                            <td className="px-4 py-3 text-xs">{item.submission?.trade_license_number || '—'}</td>
                            <td className="px-4 py-3 text-xs">{item.submission?.business_registration_number || '—'}</td>
                            <td className="px-4 py-3">
                              <div className="flex gap-2 flex-wrap">
                                <Button size="sm" variant="outline"
                                  onClick={() => handleViewDocuments(item.user_id)}>
                                  {t('admin.view_document')}
                                </Button>
                                <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white"
                                  onClick={() => {
                                    setVerifAction({ userId: item.user_id, name: item.name, action: 'approve' });
                                    setVerifGrantLicense(!!item.submission?.request_license);
                                    setVerifGrantBusiness(!!item.submission?.request_business);
                                  }}>
                                  {t('admin.approve')}
                                </Button>
                                <Button size="sm" variant="destructive"
                                  onClick={() => { setVerifAction({ userId: item.user_id, name: item.name, action: 'reject' }); setVerifRejectReason(''); }}>
                                  {t('admin.reject')}
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
          {/* ── Client verifications tab ── */}
          <TabsContent value="client-verifications" className="mt-6">
            {clientVerifLoading ? (
              <p className="text-center text-muted-foreground py-8">Loading…</p>
            ) : clientVerifQueue.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">{t('verification.queue_empty')}</p>
            ) : (
              <Card>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-gray-50 text-xs text-muted-foreground">
                          <th className="text-left px-4 py-3">Client</th>
                          <th className="text-left px-4 py-3">Email verified</th>
                          <th className="text-left px-4 py-3">ID type</th>
                          <th className="text-left px-4 py-3">Submitted</th>
                          <th className="text-left px-4 py-3">Document</th>
                          <th className="text-left px-4 py-3">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clientVerifQueue.map(item => (
                          <tr key={item.user_id} className="border-b last:border-0 hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <div className="font-medium">{item.name}</div>
                              <div className="text-xs text-muted-foreground">{item.email}</div>
                            </td>
                            <td className="px-4 py-3">
                              {item.email_verified
                                ? <span className="text-green-600">✓</span>
                                : <span className="text-red-600">✗</span>}
                            </td>
                            <td className="px-4 py-3">{item.doc_type || '—'}</td>
                            <td className="px-4 py-3 text-xs">{item.submitted_at ? new Date(item.submitted_at).toLocaleDateString(i18n.language) : '—'}</td>
                            <td className="px-4 py-3">
                              {item.document_signed_url ? (
                                <a href={item.document_signed_url} target="_blank" rel="noopener noreferrer"
                                  className="text-blue-600 hover:underline text-xs">
                                  {t('admin.view_document')}
                                </a>
                              ) : '—'}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex gap-2 flex-wrap">
                                <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white"
                                  onClick={() => handleClientVerifAction(item.user_id, 'approve')}>
                                  {t('admin.approve')}
                                </Button>
                                <Button size="sm" variant="destructive"
                                  onClick={() => { setClientVerifAction({ userId: item.user_id, name: item.name }); setClientRejectReason(''); }}>
                                  {t('admin.reject')}
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
          {/* ── Flags tab ── */}
          <TabsContent value="flags" className="mt-6">
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-muted-foreground">{flags.length} unreviewed flag(s)</p>
              <Button variant="outline" size="sm" onClick={fetchFlags}>Refresh</Button>
            </div>
            {flagsLoading ? (
              <p className="text-center text-muted-foreground py-8">Loading…</p>
            ) : flags.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No unreviewed flags</p>
            ) : (
              <Card>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-gray-50 text-xs text-muted-foreground">
                          <th className="text-left px-4 py-3">Type</th>
                          <th className="text-left px-4 py-3">User</th>
                          <th className="text-left px-4 py-3">Reason</th>
                          <th className="text-left px-4 py-3">Date</th>
                          <th className="text-left px-4 py-3">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {flags.map(flag => {
                          const flagTypeKey = {
                            excessive_cancellations: 'admin.flag_excessive_cancellations',
                            duplicate_account: 'admin.flag_duplicate_account',
                            failed_payment: 'admin.flag_failed_payment',
                            suspicious_activity: 'admin.flag_suspicious',
                          }[flag.flag_type] || flag.flag_type;
                          return (
                            <tr key={flag.id} className="border-b last:border-0 hover:bg-gray-50">
                              <td className="px-4 py-3">
                                <span className="px-2 py-0.5 bg-orange-100 text-orange-800 rounded-full text-xs font-medium">
                                  {t(flagTypeKey)}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <div className="font-medium">{flag.user_name || flag.user_id}</div>
                                <div className="text-xs text-muted-foreground">{flag.user_email}</div>
                              </td>
                              <td className="px-4 py-3 text-xs max-w-[180px] truncate" title={flag.reason}>
                                {flag.reason}
                              </td>
                              <td className="px-4 py-3 text-xs">
                                {flag.created_at ? new Date(flag.created_at).toLocaleDateString(i18n.language) : '—'}
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex gap-2">
                                  <Button size="sm" variant="outline"
                                    onClick={() => handleFlagAction(flag.id, 'dismiss')}>
                                    {t('admin.dismiss')}
                                  </Button>
                                  <Button size="sm" variant="destructive"
                                    onClick={() => setSuspendConfirm(flag)}>
                                    {t('admin.suspend_user')}
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>

    {/* Suspend confirmation dialog */}
    <Dialog open={!!suspendConfirm} onOpenChange={() => setSuspendConfirm(null)}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t('admin.suspend_user')}</DialogTitle>
        </DialogHeader>
        <p className="text-sm">{t('admin.suspend_confirm')}</p>
        <p className="text-sm font-medium">{suspendConfirm?.user_name} — {suspendConfirm?.user_email}</p>
        <div className="flex gap-2 justify-end pt-2">
          <Button variant="outline" size="sm" onClick={() => setSuspendConfirm(null)}>Cancel</Button>
          <Button size="sm" variant="destructive"
            onClick={() => handleFlagAction(suspendConfirm.id, 'suspend_user')}>
            {t('admin.suspend_user')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>

    {/* Verification action dialog */}
    <Dialog open={!!verifAction} onOpenChange={() => setVerifAction(null)}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>
            {verifAction?.action === 'approve' ? t('admin.approve') : t('admin.reject')} — {verifAction?.name}
          </DialogTitle>
        </DialogHeader>
        {verifAction?.action === 'approve' ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Approving marks this provider as <strong>Verified</strong>. Optionally grant badges:</p>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={verifGrantLicense}
                onChange={e => setVerifGrantLicense(e.target.checked)} />
              {t('verification.badge_licensed')}
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={verifGrantBusiness}
                onChange={e => setVerifGrantBusiness(e.target.checked)} />
              {t('verification.badge_business')}
            </label>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" size="sm" onClick={() => setVerifAction(null)}>Cancel</Button>
              <Button size="sm" disabled={verifSubmitting} onClick={handleVerifAction}
                className="bg-green-600 hover:bg-green-700 text-white">
                {verifSubmitting ? 'Saving…' : t('admin.approve')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <label className="text-sm font-medium">{t('admin.rejection_reason')}</label>
            <textarea className="w-full border rounded-md px-3 py-2 text-sm" rows={3} maxLength={500}
              value={verifRejectReason} onChange={e => setVerifRejectReason(e.target.value)} />
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" size="sm" onClick={() => setVerifAction(null)}>Cancel</Button>
              <Button size="sm" variant="destructive" disabled={verifSubmitting || !verifRejectReason.trim()}
                onClick={handleVerifAction}>
                {verifSubmitting ? 'Saving…' : t('admin.reject')}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>

    {/* Client verification reject dialog */}
    <Dialog open={!!clientVerifAction} onOpenChange={() => setClientVerifAction(null)}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t('admin.reject')} — {clientVerifAction?.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <label className="text-sm font-medium">{t('admin.rejection_reason')}</label>
          <textarea className="w-full border rounded-md px-3 py-2 text-sm" rows={3} maxLength={500}
            value={clientRejectReason} onChange={e => setClientRejectReason(e.target.value)} />
          <div className="flex gap-2 justify-end pt-2">
            <Button variant="outline" size="sm" onClick={() => setClientVerifAction(null)}>Cancel</Button>
            <Button size="sm" variant="destructive" disabled={!clientRejectReason.trim()}
              onClick={() => handleClientVerifAction(clientVerifAction.userId, 'reject', clientRejectReason)}>
              {t('admin.reject')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Dispute resolution dialog */}
    <Dialog open={!!disputeJob} onOpenChange={() => { setDisputeJob(null); setDisputeNote(''); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t('admin.resolve_dispute')}</DialogTitle>
          <p className="text-xs text-muted-foreground">{disputeJob?.client_name} ↔ {disputeJob?.provider_name}</p>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-sm font-medium">Outcome</label>
            <select className="w-full border rounded-md px-3 py-2 text-sm mt-1"
              value={disputeOutcome} onChange={e => setDisputeOutcome(e.target.value)}>
              <option value="favour_client">{t('admin.favour_client')}</option>
              <option value="favour_provider">{t('admin.favour_provider')}</option>
              <option value="cancelled_no_fault">{t('admin.no_fault')}</option>
            </select>
          </div>
          <div>
            <label className="text-sm font-medium">Resolution note</label>
            <textarea className="w-full border rounded-md px-3 py-2 text-sm mt-1" rows={3} maxLength={1000}
              value={disputeNote} onChange={e => setDisputeNote(e.target.value)} />
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <Button variant="outline" size="sm" onClick={() => { setDisputeJob(null); setDisputeNote(''); }}>Cancel</Button>
            <Button size="sm" disabled={disputeSubmitting || !disputeNote.trim()} onClick={handleResolveDispute}>
              {disputeSubmitting ? 'Saving…' : t('admin.resolve_dispute')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Chat modal — read-only */}
    <Dialog open={!!chatJob} onOpenChange={() => { setChatJob(null); setChatMessages([]); }}>
      <DialogContent className="max-w-lg max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{t('admin.view_chat')}</DialogTitle>
          <p className="text-xs text-muted-foreground">{t('admin.read_only_label')}</p>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto space-y-2 py-2 min-h-0" style={{ maxHeight: '55vh' }}>
          {chatLoading ? (
            <p className="text-center text-sm text-muted-foreground py-4">Loading…</p>
          ) : chatMessages.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-4">No messages yet</p>
          ) : chatMessages.map(msg => (
            <div key={msg.id} className="bg-gray-50 rounded-lg px-3 py-2">
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs font-semibold">{msg.sender_name}</span>
                <span className="text-xs text-muted-foreground">
                  {msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' }) : ''}
                </span>
              </div>
              <p className="text-sm">{msg.content}</p>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default AdminManagementPage;
